import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { Modal } from 'bootstrap';
import {Row, Col, Card } from 'react-bootstrap';
import Swal from 'sweetalert2';
import BeatLoader from 'react-spinners/BeatLoader';

import { exportUsers, getRoleNameFromId, getStatusBadge, handleApiError, successNotification } from 'utils';

import { deleteUserById, fetchUsers } from 'api/users/users';

import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';

import Header from './header/Header';
import UsersColumns from './columns/UsersColumns';
import UserModal from '../modals/UserModal';

const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "red"
};

/**
 * @component UsersTable
 * @description Componente principal para gestionar y mostrar usuarios certificados en una tabla avanzada.
 * Permite listar, crear, editar y eliminar usuarios. Utiliza modales de Bootstrap, SweetAlert2 para confirmaciones y 
 * funcionalidades como paginación, ordenamiento y búsqueda con `AdvanceTable`.
 *
 * @returns {JSX.Element}
 * 
 * @example
 * <UsersTable />
 */
const UsersTable = () => {
	// 🔐 Token de autenticación del usuario
	const token = useSelector(state => state.authentication.user?.token);
	const user = useSelector(state => state.authentication.user);

	// 🧑 Usuarios obtenidos del backend
	const [users, setUsers] = useState([]); 
	const [rows, setRows] = useState([]); // Filas formateadas para la tabla

	const [loading, setLoading] = useState(false);

	const [isModalOpen, setIsModalOpen] = useState(false); // Modal abierto o cerrado
	const [activeModal, setActiveModal] = useState(""); // Tipo de modal activo

	const [action, setAction] = useState(""); // Acción (Nuevo | Editar)
	const [usuarioId, setUsuarioId] = useState(0); // ID del usuario en edición

	const [selectedRows, setSelectedRows] = useState({});


	/**
	 * @function useEffect (users)
	 * @description Formatea los usuarios obtenidos para la tabla, excluyendo al usuario actual.
	 */
	useEffect(() => {
		if (users.length > 0) {
			let index = 0;
			const usersFiltered = users.filter(user => user.role_id !== 1);
			const formattedUsers = usersFiltered.map(user => ({
				index: index++, 
				id: user.id, 
				username: user.username,
				email: user.email, 
				name: `${user.name} ${user.lastname}`,
				created: user.created ? moment(user.created).format('DD/MM/YYYY') : 'N/A',  
				updated: user.updated ? moment(user.updated).format('DD/MM/YYYY') : 'N/A', 
				type:  getRoleNameFromId(user.role_id),
				status: getStatusBadge(user.status)
			}));
			setRows(formattedUsers);
		}
	}, [users]);

	/**
	 * @function useEffect (modal)
	 * @description Muestra el modal de usuario cuando está abierto.
	 */
	useEffect(() => {
		if (isModalOpen && activeModal === 'Usuario') {
			let myModal = new Modal(document.getElementById('userModal'));
			myModal.show();
		} 
	}, [isModalOpen, activeModal]);

	/**
	 * @function loadCertifiedUsers
	 * @description Carga todos los usuarios certificados desde el backend.
	 */
	const loadCertifiedUsers = useCallback(async () => {
		try {
			setLoading(true);
			const { data } = await fetchUsers({ type: 'certified' }, token);
			setUsers(data || []); // 'data' ya es el arreglo de usuarios, no necesitas acceder a .message
		} catch (error) {
			handleApiError(error);
			setUsers([]);
		} finally {
			setLoading(false);
		}
	}, [token]);

	/**
	 * @function useEffect (token)
	 * @description Llama a `loadCertifiedUsers` cuando hay token disponible.
	 */
	useEffect(() => {
		if (token) loadCertifiedUsers();
	}, [token, loadCertifiedUsers]);

	/**
	 * @function handleDeleteUser
	 * @param {number} id - ID del usuario a eliminar
	 * @description Elimina el usuario y recarga la lista.
	 */
	const handleDeleteUserById = async (id) => {
		try {
			const { data } = await deleteUserById(id, token);
			successNotification(data.message);
			loadCertifiedUsers(); // o el callback correspondiente
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleEdit
	 * @param {number} id - ID del usuario a editar
	 * @description Abre el modal en modo edición.
	 */
	const handleEdit = (id) => {
		setIsModalOpen(true);
		setActiveModal("Usuario");
		setAction("Editar");
		setUsuarioId(id);
	};

	/**
	 * @function handleDelete
	 * @param {number} id - ID del usuario
	 * @param {string} username - Nombre del usuario a mostrar en alerta
	 * @description Confirma la eliminación del usuario mediante SweetAlert.
	 */
	const handleDelete = (id, username) => {
		Swal.fire({
			title: `¿Deseas eliminar al usuario ${username}?`,
			text: "Una vez eliminado ya no estará disponible este usuario.",
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) confirmDeleteUser(id);
		});
	};

	/**
	 * @function confirmDeleteUser
	 * @param {number} id - ID del usuario a eliminar
	 * @description Segunda confirmación para eliminar el usuario.
	 */
	const confirmDeleteUser = (id) => {
		Swal.fire({
			title: "¿Estás seguro?",
			text: "Esta operación no se puede deshacer.",
			icon: "warning",
			confirmButtonText: 'Eliminar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons: true
		}).then((result) => {
			if (result.isConfirmed) handleDeleteUserById(id);
		});
	};

	/**
	 * @function handleSelectedChange
	 * @description
	 * Maneja el cambio de selección en la tabla. Extrae los `index` seleccionados desde `selectedData`
	 * y filtra las filas (`rows`) que coincidan con esos índices. Actualiza el estado `selectedRows` con las filas seleccionadas.
	 *
	 * Esta función está memorizada con `useCallback` para evitar recrearse innecesariamente,
	 * y depende del estado actual de `rows`.
	 *
	 * @param {Object} selectedData - Objeto con las claves de las filas seleccionadas, por ejemplo: { '0': true, '2': true }.
	 * Las claves representan los índices (`index`) de las filas seleccionadas.
	 *
	 * @returns {void}
	 */
	const handleSelectedChange = useCallback((selectedData) => {
		const selectedIds = Object.keys(selectedData).map(id => parseInt(id, 10));
		setSelectedRows(rows.filter(row => selectedIds.includes(row.index)));
	}, [rows]);

	/**
	 * @function handleBulkAction
	 * @description
	 * Ejecuta acciones en lote según la selección (exportar, eliminar, etc).
	 *
	 * @param {string} action - Tipo de acción ('export', 'delete', etc.)
	 */
	const handleBulkAction = (action) => {
		if (action === 'export') {
			handleExportUsers();
		} else if (action === 'delete') {
			handleBulkDelete();
		}
	};

	const handleBulkDelete = () => {
		Swal.fire({
			title: '¿Estás seguro de eliminar los usuarios seleccionados?',
			text: 'Esta acción eliminará permanentemente a los usuarios seleccionados del sistema. No podrás deshacer esta operación.',
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) 
				for (const item of selectedRows) {
					handleDeleteUserById(item.id);
				}
		}).catch(() => {
			return false;
		})
	}

	/**
	 * 
	 */
	const handleExportUsers = () => {
		Swal.fire({
			title: '¿Deseas descargar el reporte de los usuarios?',
			text: 'Se generará un archivo con la información detallada de los usuarios.',
			icon: "question",
			confirmButtonText: 'Continuar',
			cancelButtonText: 'Cancelar',
			showCancelButton: true,
			showCloseButton: true,
			allowOutsideClick: false,
			allowEscapeKey: false,
			allowEnterKey: false,
			reverseButtons : true
		}).then((result) => {
			if (result.isConfirmed) 
				exportUsers(selectedRows);
		}).catch(() => {
			return false;
		})
	}

	/**
	 * @function handleOpenModal
	 * @description Abre el modal para registrar un nuevo usuario.
	 */
	const handleOpenModal = () => {
		setIsModalOpen(true);
		setActiveModal("Usuario");
		setAction("Nuevo");
		setUsuarioId(0);
	};

	/**
	 * @function handleCloseModal
	 * @description Cierra el modal y recarga la lista de usuarios.
	 */
	const handleCloseModal = () => {
		setIsModalOpen(false);
		setActiveModal("");
		document.body.style.overflow = 'auto';
		loadCertifiedUsers();
	};

	return (
		<Row className="gx-3">
			<Col xxl={12} xl={12}>
				<AdvanceTableWrapper
					columns={UsersColumns(handleEdit, handleDelete)}
					data={rows}
					selection
					selectionColumnWidth={52}
					sortable
					pagination
					perPage={11}
					rowCount={rows.length}>
					<Card>
						<Card.Header className="px-0">
							<Header table handleSelectedChange={handleSelectedChange}  handleOpen={handleOpenModal} onBulkAction={handleBulkAction}/>
						</Card.Header>
						<Card.Body className="p-0">
							{ loading ? (
								<div className="sweet-loading" style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.8)', // Puedes agregar un fondo semitransparente si lo deseas
									zIndex: 9999}} >
									<BeatLoader
										color={"#0a83e6"}
										loading={loading}
										cssOverride={override}
										size={15}
										margin={5}
										aria-label="Loading..."
										data-testid="loader"
									/>
								</div>
							) : (
								<AdvanceTable
									table
									headerClassName="bg-light text-800 align-middle"
									rowClassName="btn-reveal-trigger align-middle"
									tableProps={{
										size: 'sm',
										className: 'fs--1 mb-0 overflow-hidden'
									}} />
							)}
						</Card.Body>
						<Card.Footer>
							<AdvanceTablePagination table />
						</Card.Footer>
					</Card>
				</AdvanceTableWrapper>
			</Col>

			{/* Modal para crear/editar usuario */}
			{isModalOpen && activeModal === "Usuario" && (
				<UserModal usuario_id={usuarioId} action={action} handleClose={handleCloseModal} />
			)}
		</Row>
	);
};

export default UsersTable;